import type {
  CoverageDeductible,
  CoverageDeductibleDiscount,
} from '@/services/coverages/types';

export type DeductibleOption = {
  value: string;
  label: string;
  minValue: number;
  code: string;
  /**
   * Id do item no catálogo — identifica a entrada exata, já que o `code`
   * pode se repetir entre grupos de atividade com descontos diferentes.
   */
  id?: string;
  maxSi?: number | null;
  minSi?: number | null;
  discount?: CoverageDeductibleDiscount[] | null;
};

export function mapDeductibleOptions(
  deductibles: CoverageDeductible[]
): DeductibleOption[] {
  if (!deductibles || deductibles.length === 0) return [];

  // Franquias de mesmo código podem se repetir entre grupos de atividade com
  // faixas de desconto diferentes, ou entre faixas de SI distintas (ex.: uma
  // entrada para 0–5M e outra para 5M–15M, ambas com a MESMA tabela de
  // desconto) — nesses casos todas devem ser exibidas. Só é descartada a
  // entrada realmente idêntica (mesmo código, mesma faixa de SI e mesmos
  // descontos).
  const discountSignature = (d: CoverageDeductible) =>
    (d.discount ?? [])
      .map(entry => `${entry.siMin}~${entry.siMax}~${entry.value}`)
      .join('|');

  const seen = new Set<string>();
  const kept = deductibles.filter(d => {
    const key = `${d.code}::${d.minSi ?? ''}~${d.maxSi ?? ''}::${discountSignature(d)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const codeCount = new Map<string, number>();
  kept.forEach(d => {
    const code = String(d.code);
    codeCount.set(code, (codeCount.get(code) ?? 0) + 1);
  });

  return kept.map(d => ({
    // O value precisa ser único no Select: código repetido (descontos
    // distintos por grupo de atividade) ganha o sufixo do id do catálogo.
    // Código único mantém o value simples, que casa com o `codeDeductible`
    // persistido nas policies.
    value:
      (codeCount.get(String(d.code)) ?? 0) > 1
        ? `${d.code}::${d.id}`
        : String(d.code),
    label: d.name,
    minValue: d.minValue,
    code: d.code,
    id: d.id,
    maxSi: d.maxSi,
    minSi: d.minSi,
    discount: d.discount,
  }));
}
